import { useMemo } from 'react';
import { useStore } from 'zustand';
import { ModelType, PrivateDistance, TrimType } from '../../../types';
import { getStoreByKey } from './store/CarouselStore';
import { CarouselFieldsEnum } from './types/CarouselTypes';

export const useSortedModels = (uniqId: string): ModelType[] => {
  const {
    filteredModels,
    options,
    sortField,
    sortDirection,
    showPrivateMileages,
    selectedPrivateDistance,
  } = useStore(getStoreByKey(uniqId));

  const sortedModels = useMemo(() => {
    const modelsToSort = [...filteredModels];

    const allowSorting = options?.fields?.includes(
      CarouselFieldsEnum.ALLOW_SORTING,
    );

    // if we dont sort fallback to default weight sort.
    if (!allowSorting || !sortField) {
      return modelsToSort.sort((a, b) => (a.weight > b.weight ? 1 : -1));
    }

    return modelsToSort.sort((a, b) => {
      if (sortField === CarouselFieldsEnum.PRIVATE_LEASING) {
        const aShowsLeasing = a.show_privatleasing;
        const bShowsLeasing = b.show_privatleasing;

        if (aShowsLeasing && !bShowsLeasing) {
          return -1;
        }

        if (!aShowsLeasing && bShowsLeasing) {
          return 1;
        }
      }

      const sortModifier = sortDirection === 'asc' ? 1 : -1;

      let valA: number | string = 0;
      let valB: number | string = 0;

      const getPrivateLeasingPrice = (car: TrimType) => {
        const leasingData = car?.private_leasing?.[0];
        if (!leasingData) return 0;

        if (showPrivateMileages) {
          switch (selectedPrivateDistance) {
            case PrivateDistance.Two:
              return leasingData.manadshyra364500 ?? 0;
            case PrivateDistance.Three:
              return leasingData.manadshyra366000 ?? 0;
            case PrivateDistance.Four:
              return leasingData.manadshyra369000 ?? 0;
            default:
              return leasingData.manadshyra363000 ?? 0;
          }
        }
        return leasingData.manadshyra363000 ?? 0;
      };

      switch (sortField) {
        case CarouselFieldsEnum.FROM_PRICE:
          valA = (a.first_car as TrimType)?.price ?? 0;
          valB = (b.first_car as TrimType)?.price ?? 0;
          break;
        case CarouselFieldsEnum.PRIVATE_LEASING:
          valA = getPrivateLeasingPrice(a.first_car as TrimType);
          valB = getPrivateLeasingPrice(b.first_car as TrimType);
          break;
        case CarouselFieldsEnum.BUSINESS_LEASE:
          valA =
            (a.first_car as TrimType)?.business_leasing?.[0]
              ?.s_6000_inkl_forsakring ?? 0;
          valB =
            (b.first_car as TrimType)?.business_leasing?.[0]
              ?.s_6000_inkl_forsakring ?? 0;
          break;
        case CarouselFieldsEnum.BENEFICIAL_VALUE:
          valA = (a.first_car as TrimType)?.beneficial_value ?? 0;
          valB = (b.first_car as TrimType)?.beneficial_value ?? 0;
          break;
      }

      const aHasValue = valA !== 0;
      const bHasValue = valB !== 0;

      if (!aHasValue && bHasValue) return 1;
      if (aHasValue && !bHasValue) return -1;
      if (!aHasValue && !bHasValue) return 0;

      if (valA < valB) {
        return -1 * sortModifier;
      }
      if (valA > valB) {
        return 1 * sortModifier;
      }

      return 0;
    });
  }, [
    filteredModels,
    options,
    sortField,
    sortDirection,
    showPrivateMileages,
    selectedPrivateDistance,
  ]);

  return sortedModels;
};
